import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, LayoutDashboard, Flame, LineChart } from 'lucide-react';
import ModalDrawer from './ModalDrawer.js';
import { CheckRow } from './Switch';

/**
 * Dashboard customization: how dense the cards are, and which widgets show.
 *
 * The density choice is Variant C's `.dens-picker` — two buttons that each
 * *draw* the layout they produce as a small wireframe, because "Full" and
 * "Compact" are meaningless words until you see what moves. The wireframes are
 * the demo's own, on tokens, so they follow the theme.
 *
 * The widget list is grouped the way the demo groups it, in `.tog-group`s with
 * a titled rule: the six metric tiles, then the FIRE summary, then the charts.
 * Ten switches in one undifferentiated column is a list you have to read from
 * the top every time to find anything. Group membership is declared here rather
 * than inferred from the key, so adding a widget is one line in one place — and
 * anything not listed still appears, under "Other", instead of disappearing.
 */

const FullWireframe = () => (
    <svg viewBox="0 0 150 56" aria-hidden="true">
        <rect x="1" y="1" width="148" height="54" rx="9" style={{ fill: 'var(--fill)', stroke: 'var(--line)' }} />
        <rect x="11" y="9" width="18" height="18" rx="6" fill="var(--sky)" opacity=".4" />
        <rect x="11" y="33" width="42" height="4" rx="2" style={{ fill: 'var(--track)' }} />
        <rect x="11" y="41" width="66" height="8" rx="4" style={{ fill: 'var(--text-faint)' }} />
        <rect x="124" y="11" width="12" height="3" rx="1.5" style={{ fill: 'var(--track)' }} />
    </svg>
);

const CompactWireframe = () => (
    <svg viewBox="0 0 150 56" aria-hidden="true">
        <rect x="1" y="1" width="148" height="25" rx="8" style={{ fill: 'var(--fill)', stroke: 'var(--line)' }} />
        <rect x="8" y="6" width="15" height="15" rx="5" fill="var(--sky)" opacity=".4" />
        <rect x="29" y="8" width="30" height="3.5" rx="1.75" style={{ fill: 'var(--track)' }} />
        <rect x="29" y="15" width="48" height="6" rx="3" style={{ fill: 'var(--text-faint)' }} />
        <rect x="1" y="30" width="148" height="25" rx="8" style={{ fill: 'var(--fill)', stroke: 'var(--line)' }} />
        <rect x="8" y="35" width="15" height="15" rx="5" fill="var(--violet)" opacity=".4" />
        <rect x="29" y="37" width="30" height="3.5" rx="1.75" style={{ fill: 'var(--track)' }} />
        <rect x="29" y="44" width="48" height="6" rx="3" style={{ fill: 'var(--text-faint)' }} />
    </svg>
);

const DashboardCustomizationModal = ({ isOpen, onClose, widgetVisibility, onVisibilityChange, cardDensity = 'full', onDensityChange, onConfirm, theme }) => {
    const { t } = useTranslation();

    const widgetLabels = {
        liquidAssetsCard: t('dashboard.liquidAssets'),
        portfolioValueCard: t('dashboard.netPortfolioValue'),
        monthlyCashflowCard: t('dashboard.monthlyCashflow'),
        annualAppreciationCard: t('dashboard.annualAppreciation'),
        equityRatioCard: t('dashboard.equityRatio'),
        totalIncomeCard: t('dashboard.totalAnnualIncome'),
        assetAllocationWidget: t('dashboard.allocationTitle'),
        sankeyChartWidget: t('dashboard.cashflowTitle'),
        projectionChartWidget: t('dashboard.projectionTitle'),
        fireSummaryCard: t('fire.widgetTitle'),
    };

    const groups = [
        {
            key: 'basic',
            title: t('dashboard.widgetGroupBasic'),
            icon: <LayoutDashboard style={{ stroke: 'var(--sky)' }} />,
            keys: ['liquidAssetsCard', 'portfolioValueCard', 'monthlyCashflowCard',
                   'annualAppreciationCard', 'equityRatioCard', 'totalIncomeCard'],
        },
        {
            key: 'fire',
            title: t('dashboard.widgetGroupFire'),
            icon: <Flame style={{ stroke: 'var(--teal)' }} />,
            keys: ['fireSummaryCard'],
        },
        {
            key: 'main',
            title: t('dashboard.widgetGroupMain'),
            icon: <LineChart style={{ stroke: 'var(--violet)' }} />,
            keys: ['projectionChartWidget', 'sankeyChartWidget', 'assetAllocationWidget'],
        },
    ];

    const available = Object.keys(widgetVisibility || {});
    const grouped = groups
        .map(g => ({ ...g, keys: g.keys.filter(k => available.includes(k)) }))
        .filter(g => g.keys.length > 0);
    // Anything the groups above do not name still has to be reachable.
    const claimed = new Set(grouped.flatMap(g => g.keys));
    const ungrouped = available.filter(k => !claimed.has(k));
    if (ungrouped.length) {
        grouped.push({ key: 'other', title: t('dashboard.widgetGroupOther'), icon: null, keys: ungrouped });
    }

    const switchFor = (widgetKey) => (
        <CheckRow
            key={widgetKey}
            name={widgetKey}
            checked={!!widgetVisibility[widgetKey]}
            onChange={(e) => onVisibilityChange(widgetKey, e.target.checked)}
        >
            {widgetLabels[widgetKey] || widgetKey}
        </CheckRow>
    );

    const handleConfirm = () => {
        if (onConfirm) onConfirm();
        onClose();
    };

    const densityOption = (key, label, desc, Wireframe) => (
        <button
            type="button"
            className={`dens ${cardDensity === key ? 'on' : ''}`}
            onClick={() => onDensityChange && onDensityChange(key)}
            role="radio"
            aria-checked={cardDensity === key}
        >
            <span className="dens-tick"><Check /></span>
            <b>{label}</b>
            <Wireframe />
            <p>{desc}</p>
        </button>
    );

    return (
        <ModalDrawer
            isOpen={isOpen}
            onClose={onClose}
            title={t('dashboard.customizeTitle')}
            theme={theme}
            footer={
                // Every choice applies the moment it is made, so there is nothing
                // for a Cancel to undo — one button, and it says so.
                <button onClick={handleConfirm} className="btn btn-grad">{t('general.done')}</button>
            }
        >
            <span className="field-label">{t('dashboard.densityTitle')}</span>
            <div className="dens-picker" role="radiogroup" aria-label={t('dashboard.densityTitle')}>
                {densityOption('full', t('dashboard.densityFull'), t('dashboard.densityFullDesc'), FullWireframe)}
                {densityOption('compact', t('dashboard.densityCompact'), t('dashboard.densityCompactDesc'), CompactWireframe)}
            </div>
            <p className="hint" style={{ marginBottom: 22 }}>{t('dashboard.densityHint')}</p>

            <span className="field-label">{t('dashboard.widgetsTitle')}</span>
            {grouped.map(group => (
                <div className="tog-group" key={group.key}>
                    <div className="tog-group-title">
                        {group.icon}
                        {group.title}
                        <span className="rule" />
                    </div>
                    {group.keys.map(switchFor)}
                </div>
            ))}
        </ModalDrawer>
    );
};

export default DashboardCustomizationModal;
